import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { LoadingButton } from '@one-thd/sui-atomic-components';
import { createBrowserHistory } from 'history';
import { useAlert } from '../../hooks/useAlert';
import CardNumberInput from './CardNumberInput';
import CardPinInput from './CardPinInput';
import { VERBIAGE, FEATURE_SWITCH, ERRORS } from '../../utils/consts';
import SaveGiftCardCheckbox from './SaveGiftCardCheckbox';
import {
  getTmxId,
  isLocalHost,
  goToAuth,
  getGQLErrors
} from '../../utils/utils';
import { publishNewRelicPageAction } from '../../utils/new-relic';
import useReCaptcha from '../../hooks/useReCaptcha';
import useKeyPress from '../../hooks/useKeyPress';
import { useGiftcardBalanceCheck } from '../../hooks/useGiftcardBalanceCheck';
import {
  setScanningAnalytics,
  setSaveToAccountAnalytics
} from '../../utils/Analytics';

const CheckBalanceForm = (props) => {
  const { onSubmit, isDrawer } = props;
  const [cardInfo, setCardInfo] = useState(null);
  const reCaptchaEnabled = !!useConfigService(FEATURE_SWITCH.RECAPTCHA);
  const [gcCheckBalanceResponse, responseStatus] = useGiftcardBalanceCheck();
  const { showAlert, dismissAlert } = useAlert();
  const { data, loading, error } = responseStatus;
  const _gqlError = getGQLErrors(error)?.[0];
  // Only for errors happened in the check balance, not including save GC to account
  const [checkBalanceError, setCheckBalanceError] = useState(null);
  const isContinueDisabled = !cardInfo?.cardNumber || !cardInfo?.pinNumber || checkBalanceError;

  useEffect(() => {
    if (loading) {
      return;
    }
    let cardDetails = {};
    if (_gqlError) {
      const { errorCode, description } = _gqlError || {};
      if (ERRORS.AUTH.some((err) => err === errorCode)) {
        goToAuth();
      } else if (ERRORS.CHECK_BALANCE.some((err) => err === errorCode)) {
        setCheckBalanceError(description);
        publishNewRelicPageAction({
          actionName: 'checkBalanceButtonFail',
          actionObject: {
            sectionName: 'checkBalanceSection',
            message: `Check Balance fail - ${description}`
          }
        });
        showAlert({
          error: {
            message: description
          }
        });
      } else if (ERRORS.RELOAD.some((err) => err === errorCode)) {
        cardDetails.error = description;
      } else if (ERRORS.SAVE_TO_ACCOUNT.some((err) => err === errorCode)) {
        showAlert({
          warning: {
            message: description,
            onClose: () => {}
          }
        });
      }
    }

    if (data?.balanceCheck) {
      const gcDetail = data.balanceCheck;
      if (gcDetail && gcDetail?.balanceDetails?.[0]?.cardNumber) {
        publishNewRelicPageAction({
          actionName: 'checkBalanceButtonSuccess',
          actionObject: {
            sectionName: 'checkBalanceSection',
            message: 'Check Balance success'
          }
        });
        // Always prefer the user-entered card number for the rest of the flow
        const enteredCardNumber = cardInfo?.cardNumber;
        cardDetails = {
          ...gcDetail?.balanceDetails?.[0],
          ...cardDetails,
          ...(enteredCardNumber ? { cardNumber: enteredCardNumber } : {})
        };
        onSubmit(cardDetails);
      }

      if (
        cardInfo?.isSaveCardToProfile
        && !ERRORS.CHECK_BALANCE.some((err) => err === _gqlError?.errorCode)
        && !ERRORS.SAVE_TO_ACCOUNT.some((err) => err === _gqlError?.errorCode)
      ) {
        setSaveToAccountAnalytics();
        showAlert({
          success: {
            message: VERBIAGE.saveToAccountSuccess,
            onClose: () => {}
          }
        });
      }
    }
  }, [loading, data, _gqlError]);

  const clearCheckBalanceError = () => {
    if (checkBalanceError) {
      setCheckBalanceError(null);
      dismissAlert('error');
    }
  };

  const handleCardNumber = (number) => {
    setCardInfo({
      ...cardInfo,
      cardNumber: number
    });
    clearCheckBalanceError();
  };

  const handlePin = (pin) => {
    setCardInfo({
      ...cardInfo,
      pinNumber: pin
    });
    clearCheckBalanceError();
  };

  const handleSaveToAccount = (isSaveCardToProfile) => {
    setCardInfo({
      ...cardInfo,
      isSaveCardToProfile
    });
  };

  const handleOnSubmit = async (recaptchaToken) => {
    const { cardNumber, pinNumber, isSaveCardToProfile } = cardInfo;
    if (!recaptchaToken) {
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonFail',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: 'Check Balance fail - missing recaptcha token'
        }
      });
    }
    let giftCard = {
      cardNumber,
      pinNumber
    };

    if (isSaveCardToProfile) {
      giftCard.isSaveCardToProfile = isSaveCardToProfile;
    }
    try {
      await gcCheckBalanceResponse({
        variables: {
          balanceCheck: {
            giftCard,
            cartAttr: { reCaptcha: recaptchaToken }
          },
          requestContext: {
            tmxProfileId: getTmxId(),
            experience: { type: 'GC_R' }
          }
        }
      });
    } catch (err) {
      showAlert({
        error: {
          message: VERBIAGE.technicalError
        }
      });
      publishNewRelicPageAction({
        actionName: 'checkBalanceButtonFail',
        actionObject: {
          sectionName: 'checkBalanceSection',
          message: 'Check Balance fail - exceptions'
        }
      });
    }
  };

  const { getToken } = useReCaptcha({
    onSubmit: handleOnSubmit
  });

  const handleContinue = () => {
    publishNewRelicPageAction({
      actionName: 'checkBalanceButtonClicked',
      actionObject: { sectionName: 'checkBalanceSection' }
    });
    if (isLocalHost() && !isDrawer) {
      const history = createBrowserHistory();
      history.push('/chapters/reload');
      history.go();
    } else if (reCaptchaEnabled) {
      getToken();
    } else {
      handleOnSubmit('');
    }
  };

  useKeyPress('Enter', () => {
    if (!isContinueDisabled) {
      handleContinue();
    }
  });

  const balanceFormClasses = `sui-flex sui-flex-col sui-max-w-full sui-gap-8 ${
        isDrawer && 'sui-h-full sui-overflow-auto sui-justify-between'
      }`;

  return (
    <div
      className={balanceFormClasses}
      data-testid="check-balance-components-container"
    >
      <div className="sui-flex sui-flex-col sui-max-w-full sui-gap-8">
        <CardNumberInput
          onReady={handleCardNumber}
          onInitScan={() => setScanningAnalytics()}
          onScanSuccess={() => setScanningAnalytics(true)}
        />
        <CardPinInput onReady={handlePin} />
        {!isDrawer && (
          <SaveGiftCardCheckbox
            isChecked={Boolean(cardInfo?.isSaveCardToProfile)}
            onChange={handleSaveToAccount}
          />
        )}
      </div>
      <LoadingButton
        loading={loading}
        fullWidth
        variant="primary"
        onClick={handleContinue}
        disabled={isContinueDisabled}
      >
        {VERBIAGE.continue}
      </LoadingButton>
    </div>
  );
};

CheckBalanceForm.propTypes = {
  onSubmit: PropTypes.func,
  isDrawer: PropTypes.bool
};

CheckBalanceForm.defaultProps = {
  onSubmit: () => {},
  isDrawer: false
};

export default CheckBalanceForm;
